import React, { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { leadsAPI } from '../services/api';
import DealFields, { dealPayload } from './DealFields';
import Button from './ui/Button';
import { Pencil } from 'lucide-react';

export default function LeadDeal({ lead, canEdit }) {
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const client = useQueryClient();
  const save = async e => {
    e.preventDefault(); setSaving(true); setError(''); setConfirmation('');
    try {
      await leadsAPI.update(lead.id, dealPayload(draft));
      await Promise.all(['lead', 'leads', 'salesReport', 'customer'].map(key => client.invalidateQueries({ queryKey: key === 'lead' ? [key, lead.id] : [key] })));
      setDraft(null);
      setConfirmation('Deal details saved.');
    } catch (err) { setError(err.response?.data?.error || err.response?.data?.errors?.[0]?.msg || 'Could not save deal details'); }
    finally { setSaving(false); }
  };
  return <section className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm space-y-3">
    {draft ? <form onSubmit={save} className="space-y-3">
      <DealFields value={draft} onChange={setDraft} disabled={saving} />
      {error && <p role="alert" className="text-red-700">{error}</p>}
      <Button disabled={saving} size="sm" type="submit">{saving ? 'Saving...' : 'Save deal'}</Button>
      <button disabled={saving} className="ml-4" type="button" onClick={() => setDraft(null)}>Cancel</button>
    </form> : <>
      <div className="flex items-center justify-between gap-3">
        <div><h3 className="text-sm font-semibold text-gray-900">Deal details</h3><p className="mt-0.5 text-xs text-gray-500">Value, timing and fulfilment</p></div>
        {canEdit && <button aria-label="Edit deal details" className="inline-flex min-h-[36px] items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-primary-700 shadow-sm hover:bg-primary-50" onClick={() => { setError(''); setConfirmation(''); setDraft(lead); }}><Pencil aria-hidden="true" className="h-3.5 w-3.5" /> Edit</button>}
      </div>
      {confirmation && <p role="status" aria-live="polite" className="text-sm font-medium text-green-700">{confirmation}</p>}
      {lead.dealValue == null && lead.recurringValue == null && !lead.closingDate && !lead.fulfilmentMethod && !lead.pickupDate && !lead.lostReason
        ? <p className="rounded-lg bg-gray-50 px-3 py-3 text-sm text-gray-500">No deal details yet.</p>
        : <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
          {lead.dealValue != null && <div><dt className="text-xs text-gray-500">One-time value</dt><dd className="font-medium text-gray-900">{lead.currency} {Number(lead.dealValue).toLocaleString(undefined, { minimumFractionDigits: 2 })}</dd></div>}
          {lead.recurringValue != null && <div><dt className="text-xs text-gray-500">Recurring fee</dt><dd className="font-medium text-gray-900">{lead.currency} {Number(lead.recurringValue).toLocaleString(undefined, { minimumFractionDigits: 2 })} / {lead.billingInterval}</dd></div>}
          {lead.closingDate && <div><dt className="text-xs text-gray-500">Expected close</dt><dd>{lead.closingDate}</dd></div>}
          {lead.fulfilmentMethod && <div><dt className="text-xs text-gray-500">Fulfilment</dt><dd>{lead.fulfilmentMethod === 'pickup' ? 'Store pickup' : 'Delivery'}</dd></div>}
          {lead.pickupDate && <div><dt className="text-xs text-gray-500">Planned pickup</dt><dd>{lead.pickupDate}</dd></div>}
          {lead.lostReason && <div className="col-span-2"><dt className="text-xs text-gray-500">Lost reason</dt><dd className="break-words text-gray-900">{lead.lostReason}</dd></div>}
        </dl>}

    </>}
  </section>;
}
